import React, { useEffect, useState } from 'react';
import { Layout, Nav, Toast, Modal } from '@douyinfe/semi-ui';
import { IconSetting, IconUser, IconHistogram, IconLayers, IconPriceTag, IconLock, IconCustomerSupport, IconShield } from '@douyinfe/semi-icons';
import { API, apiErrMsg } from '../helpers';
import { useHashRoute, navigate } from '../router';
import { useSite } from '../context/SiteContext';
import AdminTicketsBell from '../components/AdminTicketsNavButton';

const { Header, Sider, Content } = Layout;

const NAV_ITEMS = [
  { itemKey: 'stats',    text: '网关总览', icon: <IconHistogram /> },
  { itemKey: 'channels', text: '渠道管理', icon: <IconLayers /> },
  { itemKey: 'tokens',    text: '令牌管理', icon: <IconUser /> },
  { itemKey: 'plans',    text: '套餐管理', icon: <IconPriceTag /> },
  { itemKey: 'tickets',  text: '工单管理', icon: <IconCustomerSupport /> },
  { itemKey: 'risk',     text: '风控词库', icon: <IconShield /> },
  { itemKey: 'settings', text: '系统设置', icon: <IconSetting /> },
];

// 移动端底部导航栏:五个主页面。
//
// 为什么单独列一份而不是复用 NAV_ITEMS:
//   1) 桌面端是左侧竖排导航(全称:网关总览/渠道管理/…),
//      手机上横排 5 个格子放不下全称,底部用短名(总览/渠道/…)才不挤;
//   2) 「系统设置」按要求留在顶栏的下拉菜单里(它不是天天点的主页面),
//      所以底部只留这 5 个主入口。
const BOTTOM_NAV = [
  { key: 'stats',    text: '总览', icon: <IconHistogram />,        path: '/admin/stats' },
  { key: 'channels', text: '渠道', icon: <IconLayers />,          path: '/admin/channels' },
  { key: 'tokens',   text: '令牌', icon: <IconUser />,            path: '/admin/tokens' },
  { key: 'plans',    text: '套餐', icon: <IconPriceTag />,        path: '/admin/plans' },
  { key: 'tickets',  text: '工单', icon: <IconCustomerSupport />, path: '/admin/tickets' },
];

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < breakpoint);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
}

const AdminLayout = ({ children }) => {
  const path = useHashRoute();
  const { site } = useSite();
  const [checking, setChecking] = useState(true);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [pwdSaving, setPwdSaving] = useState(false);
  // 移动端顶栏的「更多」下拉(系统设置/修改密码/退出登录)
  const [menuOpen, setMenuOpen] = useState(false);
  // 受控字段:避开 Semi Form 的 useForm(useFormApi 在 2.x 里不能挂在 Form 上)
  const [pwd, setPwd] = useState({ old: '', neu: '', confirm: '' });

  const isMobile = useIsMobile();

  // 检查登录态:任何非成功响应都跳回登录页
  useEffect(() => {
    API.get('/api/admin/me')
      .then((r) => { if (!r.data.success) navigate('/admin/login'); })
      .catch(() => navigate('/admin/login'))
      .finally(() => setChecking(false));
  }, []);

  const handleLogout = async () => {
    try {
      await API.post('/api/admin/logout');
      Toast.success('已登出');
    } catch (e) {
      Toast.error(apiErrMsg(e));
    }
    navigate('/admin/login');
  };

  const openPwd = () => {
    setPwd({ old: '', neu: '', confirm: '' });
    setPwdOpen(true);
  };
  const closePwd = () => {
    setPwdOpen(false);
  };

  // 提交改密:成功后后端会清空所有 session 并清 cookie,前端需跳回登录页
  const handleChangePassword = async () => {
    if (!pwd.old || !pwd.neu || !pwd.confirm) {
      Toast.error('请填写完整三项');
      return;
    }
    if (pwd.neu !== pwd.confirm) {
      Toast.error('两次输入的新密码不一致');
      return;
    }
    if (pwd.neu.length < 8) {
      Toast.error('新密码至少 8 位');
      return;
    }
    setPwdSaving(true);
    try {
      await API.post('/api/admin/password', {
        old_password: pwd.old,
        new_password: pwd.neu,
      });
      Toast.success('密码已修改,请用新密码重新登录');
      setPwdOpen(false);
      // 后端已清 cookie,直接跳登录页
      setTimeout(() => navigate('/admin/login'), 600);
    } catch (e) {
      Toast.error(e.response?.data?.message || '修改失败');
    } finally {
      setPwdSaving(false);
    }
  };

  if (checking) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#86909c' }}>加载中…</div>;
  }
  // 登录态由 useEffect 处理:这里不再依赖 me

  const selectedKey = (() => {
    if (path.startsWith('/admin/stats'))     return 'stats';
    if (path.startsWith('/admin/channels'))  return 'channels';
    if (path.startsWith('/admin/tokens'))     return 'tokens';
    if (path.startsWith('/admin/plans'))      return 'plans';
    if (path.startsWith('/admin/tickets'))    return 'tickets';
    if (path.startsWith('/admin/risk'))       return 'risk';
    if (path.startsWith('/admin/settings'))   return 'settings';
    return 'stats';
  })();

  const handleNavSelect = (k) => {
    if (k.itemKey === 'stats')    navigate('/admin/stats');
    if (k.itemKey === 'channels') navigate('/admin/channels');
    if (k.itemKey === 'tokens')    navigate('/admin/tokens');
    if (k.itemKey === 'plans')    navigate('/admin/plans');
    if (k.itemKey === 'tickets')  navigate('/admin/tickets');
    if (k.itemKey === 'risk')     navigate('/admin/risk');
    if (k.itemKey === 'settings') navigate('/admin/settings');
  };

  // 通用行样式
  const labelStyle = { display: 'block', fontSize: 13, color: '#4e5969', marginBottom: 6 };
  const inputStyle = {
    width: '100%', height: 32, padding: '0 10px',
    border: '1px solid #e5e6eb', borderRadius: 4,
    fontSize: 14, outline: 'none', boxSizing: 'border-box',
  };

  const navEl = (
    <Nav
      selectedKeys={[selectedKey]}
      style={{ height: '100%', width: '100%' }}
      onSelect={handleNavSelect}
      items={NAV_ITEMS}
    />
  );

  // 顶栏「更多」下拉里的菜单项(移动端专用)
  const headerMenuItem = (icon, label, onClick, color) => (
    <button
      type="button"
      onClick={() => { setMenuOpen(false); onClick(); }}
      style={{
        display: 'flex', alignItems: 'center', gap: 8, width: '100%',
        padding: '10px 14px', background: 'transparent', border: 'none',
        fontSize: 13, color: color || '#52525b', cursor: 'pointer', textAlign: 'left',
      }}
    >
      {icon}
      {label}
    </button>
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{
        background: '#ffffff',
        borderBottom: '1px solid #e5e7eb',
        padding: isMobile ? '0 12px' : '0 24px',
        height: 56,
        lineHeight: '56px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'relative', // 让移动端「更多」下拉面板相对顶栏定位
      }}>
        <a href="/" style={{ color: '#18181b', display: 'flex', alignItems: 'center', gap: isMobile ? 6 : 10, textDecoration: 'none', cursor: 'pointer' }}>
          <img src="/logo.svg" alt="" style={{ width: 28, height: 28 }} />
          <strong style={{ fontSize: 16, fontWeight: 600 }}>{isMobile ? site.site_name : `${site.site_name} · 管理后台`}</strong>
        </a>
        {!isMobile ? (
          <div style={{ color: '#52525b', fontSize: 13, display: 'flex', alignItems: 'center', gap: 16 }}>
            <AdminTicketsBell />
            <a
              href="#password"
              onClick={(e) => { e.preventDefault(); openPwd(); }}
              style={{ color: '#52525b', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              <IconLock style={{ marginRight: 4, verticalAlign: -2 }} />修改密码
            </a>
            <a
              href="#logout"
              onClick={(e) => { e.preventDefault(); handleLogout(); }}
              style={{ color: '#52525b', textDecoration: 'none' }}
            >
              退出
            </a>
          </div>
        ) : (
          /* 移动端顶栏:工单铃铛 + 「更多」(系统设置/修改密码/退出登录)。
             这三项按要求仍然留在顶部,不挪到底部栏 —— 底部只放五个主页面。 */
          <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <AdminTicketsBell />
            <button
              type="button"
              aria-label="更多"
              onClick={() => setMenuOpen((v) => !v)}
              style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                padding: '6px 8px', color: '#52525b', fontSize: 20, lineHeight: 1,
                display: 'inline-flex', alignItems: 'center',
              }}
            >
              <IconSetting />
            </button>
            {menuOpen && (
              <>
                {/* 透明遮罩:点外面关闭下拉 */}
                <div
                  onClick={() => setMenuOpen(false)}
                  style={{ position: 'fixed', left: 0, top: 0, right: 0, bottom: 0, zIndex: 1000 }}
                />
                <div style={{
                  position: 'absolute', right: 12, top: 52, zIndex: 1001,
                  background: '#fff', border: '1px solid #e5e6eb', borderRadius: 8,
                  boxShadow: '0 6px 20px rgba(0,0,0,0.12)', minWidth: 150,
                  padding: '4px 0', overflow: 'hidden',
                }}>
                  {headerMenuItem(<IconShield />, '风控词库', () => navigate('/admin/risk'))}
                  {headerMenuItem(<IconSetting />, '系统设置', () => navigate('/admin/settings'))}
                  {headerMenuItem(<IconLock />, '修改密码', openPwd)}
                  {headerMenuItem(null, '退出登录', handleLogout, '#f56565')}
                </div>
              </>
            )}
          </div>
        )}
      </Header>
      <Layout>
        {!isMobile && (
          <Sider style={{ background: '#fff' }} width={200}>
            {navEl}
          </Sider>
        )}
        <Content style={{
          // 底部留出导航栏的高度,别把最后一行内容挡住。
          // 导航栏实际高度 = 按钮内容(约 50px)+ env(safe-area-inset-bottom)(iPhone 约 34px)
          // ≈ 84px,所以这里跟着 safe-area 一起算,不能写死 76px(会差 6~8px 压住最后一行)。
          padding: isMobile ? '12px 12px calc(62px + env(safe-area-inset-bottom))' : 20,
          background: '#f7f8fa',
        }}>
          {children}
        </Content>
      </Layout>

      {/* 移动端底部导航栏:五个主页面,点一下就切,不用再开抽屉 */}
      {isMobile && (
        <nav
          style={{
            position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 900,
            display: 'flex', background: '#fff', borderTop: '1px solid #e5e6eb',
            paddingBottom: 'env(safe-area-inset-bottom)', // iPhone 底部横条不要压住按钮
          }}
        >
          {BOTTOM_NAV.map((it) => {
            const active = selectedKey === it.key;
            return (
              <button
                key={it.key}
                type="button"
                onClick={() => navigate(it.path)}
                style={{
                  flex: 1, minWidth: 0, background: 'transparent', border: 'none',
                  cursor: 'pointer', padding: '6px 0 8px',
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  gap: 2, color: active ? '#1d7dfa' : '#86909c',
                }}
              >
                <span style={{ fontSize: 18, lineHeight: '20px', display: 'inline-flex' }}>{it.icon}</span>
                <span style={{ fontSize: 11, lineHeight: '14px' }}>{it.text}</span>
              </button>
            );
          })}
        </nav>
      )}

      {/* 修改密码弹窗(用受控 input 避开 Semi Form 的 useForm 兼容问题) */}
      <Modal
        title="修改管理员密码"
        visible={pwdOpen}
        onCancel={closePwd}
        confirmLoading={pwdSaving}
        onOk={handleChangePassword}
        okText="确认修改"
        cancelText="取消"
        width={420}
      >
        <div style={{ marginTop: 12 }}>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>当前密码</label>
            <input
              type="password"
              style={inputStyle}
              value={pwd.old}
              onChange={(e) => setPwd({ ...pwd, old: e.target.value })}
              placeholder="请输入当前密码"
              autoFocus
            />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>新密码 <span style={{ color: '#c9cdd4', fontWeight: 'normal' }}>(至少 8 位)</span></label>
            <input
              type="password"
              style={inputStyle}
              value={pwd.neu}
              onChange={(e) => setPwd({ ...pwd, neu: e.target.value })}
              placeholder="请输入新密码"
            />
          </div>
          <div style={{ marginBottom: 6 }}>
            <label style={labelStyle}>确认新密码</label>
            <input
              type="password"
              style={inputStyle}
              value={pwd.confirm}
              onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })}
              placeholder="再次输入新密码"
              onKeyDown={(e) => { if (e.key === 'Enter') handleChangePassword(); }}
            />
          </div>
          <div className="form-hint">
            修改成功后会自动登出,需用新密码重新登录
          </div>
        </div>
      </Modal>
    </Layout>
  );
};

export default AdminLayout;
